import { CSSProperties, useEffect, useRef, useState } from "react";
import { CoinsIcon, SparklesIcon } from "lucide-react";

import { Column } from "@/components/Column";
import { ProgressRing } from "@/components/ProgressRing";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { cn } from "@/lib/utils";

const GOAL_RING_SIZE_PX = 67;
// Long enough for the shockwave + sparkles (900ms) to finish before unmount.
const CELEBRATION_MS = 1_200;
const SPARKLE_COUNT = 8;
const FULL_TURN_DEG = 360;
const SPARKLE_STAGGER_MS = 40;
const GOLD_RING_CLASS = "stroke-amber-400";

// A weekly-goal ring that turns gold once the goal is reached and plays a
// one-shot celebration the moment the count crosses it during this session.
export const WeeklyGoalRing = ({
  bonusCoins,
  colorClass,
  goal,
  label,
  noun,
  perItemNote,
  value,
}: {
  bonusCoins: number;
  colorClass: string;
  goal: number;
  label: string;
  noun: string;
  // Extra tooltip line for coins paid along the way, before the goal bonus.
  perItemNote?: string;
  value: number;
}) => {
  /* React Hooks */
  const isReached = value >= goal;
  const [isCelebrating, setIsCelebrating] = useState(false);
  const wasReachedRef = useRef(isReached);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Only a fresh crossing celebrates — mounting already past the goal
    // (earned earlier in the week) just shows gold.
    if (isReached && !wasReachedRef.current) {
      setIsCelebrating(true);
      timeoutRef.current = setTimeout(() => {
        setIsCelebrating(false);
      }, CELEBRATION_MS);
    }
    wasReachedRef.current = isReached;
  }, [isReached]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  /* Helpers */
  const isGold = isReached;
  const remaining = Math.max(goal - value, 0);

  const sparkleStyle = (index: number): CSSProperties =>
    ({
      "--angle": `${String((index * FULL_TURN_DEG) / SPARKLE_COUNT)}deg`,
      animationDelay: `${String(index * SPARKLE_STAGGER_MS)}ms`,
    }) as CSSProperties;

  /* Render */
  const renderSparkles = () => {
    return Array.from({ length: SPARKLE_COUNT }).map((_, index) => (
      <SparklesIcon
        key={index}
        className="weekly-goal-sparkle absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 fill-amber-300 text-amber-500"
        style={sparkleStyle(index)}
      />
    ));
  };

  const renderCelebration = () => {
    if (!isCelebrating) return null;
    return (
      <div className="pointer-events-none absolute inset-0 overflow-visible">
        <div className="weekly-goal-shockwave absolute inset-0 rounded-full border-2 border-amber-400" />
        <div className="weekly-goal-shockwave absolute inset-0 rounded-full border border-amber-300 [animation-delay:150ms]" />
        {renderSparkles()}
      </div>
    );
  };

  // Sits inside the ring's lower arc, under the count, to keep total height
  // down to just the ring + label.
  const renderBonus = () => {
    return (
      <Row
        className={cn(
          // The wrapper includes the label below the ring, so anchor from the
          // top: ~2/3 down the 67px ring lands in its lower arc.
          "pointer-events-none absolute inset-x-0 top-[45px] items-center justify-center gap-0.5",
          isGold ? "text-amber-500" : "text-slate-400",
        )}
      >
        <CoinsIcon className="h-2.5 w-2.5" />
        <Text className="text-[9px] font-normal leading-none tabular-nums text-inherit">
          {isGold ? "Earned" : `+${String(bonusCoins)}`}
        </Text>
      </Row>
    );
  };

  const renderPerItemNote = () => {
    if (!perItemNote) return null;
    return (
      <Text className="text-[11px] font-normal text-slate-500">
        {perItemNote}
      </Text>
    );
  };

  const renderTooltip = () => {
    if (isGold) {
      return (
        <Column className="gap-1">
          <Text className="text-xs text-slate-700">
            Weekly {noun} goal hit — {String(bonusCoins)} coins earned.
          </Text>
          {renderPerItemNote()}
          <Text className="text-[11px] font-normal text-slate-500">
            Resets Monday. One bonus per week.
          </Text>
        </Column>
      );
    }
    return (
      <Column className="gap-1">
        <Text className="text-xs text-slate-700">
          Hit {String(goal)} {noun} this week and earn {String(bonusCoins)}{" "}
          bonus coins for your pet.
        </Text>
        {renderPerItemNote()}
        <Text className="text-[11px] font-normal tabular-nums text-slate-500">
          {String(remaining)} to go. Counts across all jobs, Monday–Sunday.
        </Text>
      </Column>
    );
  };

  const render = () => {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Column className="items-center">
              <div
                className={cn(
                  "relative",
                  isGold && "weekly-goal-gold-glow",
                  isCelebrating && "weekly-goal-pop",
                )}
              >
                <ProgressRing
                  colorClass={isGold ? GOLD_RING_CLASS : colorClass}
                  goal={goal}
                  label={label}
                  sizePx={GOAL_RING_SIZE_PX}
                  value={value}
                />
                {renderCelebration()}
                {renderBonus()}
              </div>
            </Column>
          </TooltipTrigger>
          <TooltipContent className="max-w-56" side="bottom">
            {renderTooltip()}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  return render();
};
